<template>
  <div class="verification-workspace">
    <!-- `close-drawing` is the pop-out SUCCESS signal, not a user dismissal:
         expand replaces the floating panel, so a panel opened by an earlier
         fallback has to go when the drawing moves to its own window. -->
    <ContextPanel
      @open-drawing="openDrawing"
      @close-drawing="closeDrawing"
    />
    <WorkspacePanel
      :reference-document="activeDrawing"
      @close-reference-document="closeDrawing"
    />
    <ResultsPanel />

    <!-- Owned here rather than inside ResultsPanel: it is a modal over the whole
         workspace, and the panel that triggers it is a 340px column it must not
         be clipped by. -->
    <ReportPreview v-model="reportOpen" />

  </div>
</template>

<script>
/**
 * Verification Workspace — main view.
 *
 * Renders the three-panel shell (Context / Workspace / Results) and drives
 * the Vuex LOAD_VERIFICATION action on route entry.
 *
 * Grid: 320px | minmax(0, 1fr) | 340px — matches the Claude Design wireframe
 * (AVA Verification Wireframe.html line 69, minus the viewer-layout-owned
 * 56px nav rail which is provided by the layout wrapper).
 */
import { mapGetters } from 'vuex';
import { log as debugLog } from '@/utils/debugLogger';
import ContextPanel from '@/features/Verification/components/context/ContextPanel.vue';
import WorkspacePanel from '@/features/Verification/components/workspace/WorkspacePanel.vue';
import ResultsPanel from '@/features/Verification/components/results/ResultsPanel.vue';
import ReportPreview from '@/features/Verification/components/report/ReportPreview.vue';
import { REPORT_EVENTS } from '@/features/Verification/utils/captureEvents';
import { closeDrawingPopout } from '@/features/Verification/utils/drawingPopout';
import { EventBus } from '@/event-bus';

export default {
  name: 'VerificationWorkspace',
  components: {
    ContextPanel,
    WorkspacePanel,
    ResultsPanel,
    ReportPreview,
  },
  data() {
    return {
      reportOpen: false,
      activeDrawing: null,
    };
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      verification: 'GET_VERIFICATION',
      loading: 'IS_LOADING',
    }),
    taskId() {
      return this.$route.params.taskId;
    },
    facilityId() {
      return this.$route.params.id;
    },
  },
  watch: {
    taskId: {
      immediate: true,
      handler(newId) {
        if (!newId) return;
        this.closeDrawing();
        // The pop-out is a separate browser window and nothing in the Vue tree
        // reaches it — left alone it would sit on the second screen showing the
        // PREVIOUS task's drawing, next to this task's results. On a
        // conformance record that is not untidy, it is wrong.
        closeDrawingPopout();
        this.loadVerification(newId);
      },
    },
    // The scan catalogue lives in ROOT state and is written only by MainView /
    // GET_FACILITY_TABS_DATA, neither of which runs on this route — so without
    // this the pre-flight finds zero point clouds and silently falls back to
    // model-only mode. Immediate + watched so both cold entry and an in-app
    // facility switch are covered. Fire-and-forget: VerificationViewer watches
    // GET_SELECTED_SCAN and applies the overlay whenever the scan resolves.
    facilityId: {
      immediate: true,
      handler(id) {
        if (id) this.$store.dispatch('VERIFICATION/ENSURE_SCAN_CATALOGUE', id);
      },
    },
  },
  mounted() {
    EventBus.$on(REPORT_EVENTS.OPEN_PREVIEW, this.openReport);
  },
  // Paired teardown is not optional here. A route re-entry that leaves the old
  // handler attached gives the bus two live listeners on one channel — this
  // feature has already lost time to a superseded instance's listener still
  // running (see AVA-475_SPIKE_FINDINGS.md, stale-listener trap).
  beforeDestroy() {
    EventBus.$off(REPORT_EVENTS.OPEN_PREVIEW, this.openReport);
    closeDrawingPopout();
  },
  methods: {
    openReport() {
      debugLog('[VerificationWorkspace] opening report preview');
      this.reportOpen = true;
    },
    openDrawing(document) {
      if (!document?.id && !document?.documentId) return;
      this.activeDrawing = {
        ...document,
        id: document.id || document.documentId,
      };
    },
    // Dismisses the FLOATING PANEL only, never the pop-out window — three
    // callers rely on that separation and they are not interchangeable:
    // user dismissal (`@close-reference-document`), task switch (the `taskId`
    // watcher, which closes the pop-out too), and pop-out success
    // (`@close-drawing`, where closing the window would undo the expand).
    // Do not fold `closeDrawingPopout()` in here.
    closeDrawing() {
      this.activeDrawing = null;
    },
    loadVerification(taskId) {
      debugLog('[VerificationWorkspace] loading for task', taskId);
      this.$store.dispatch('VERIFICATION/LOAD_VERIFICATION', taskId);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/verification-tokens';

.verification-workspace {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr) 340px;
  min-height: 0;
  height: 100%;
  min-width: 1280px;
  background: $vf-bg;
}
</style>
